// One system in depth: the written readout, live numbers, agent-recorded
// metrics, and the cost / funding / benefit ledger.

import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import {
  readoutService,
  type LiveExample,
  type LiveSection,
  type MetricSeries,
  type Narrative,
  type SystemCard,
  type SystemReadout,
} from '../../services/readout';
import {
  categoryLabel,
  formatMetric,
  formatNet,
  formatStamp,
  formatUsd,
  groupLedger,
  netTone,
  periodLabel,
  systemCategoryLabel,
} from './readoutFormat';
import styles from './ReadoutPage.module.css';

interface Props {
  systemKey: string;
  windowDays: number;
  card: SystemCard | null;
}

const READER_LABELS: Record<string, string> = {
  order_pipeline: 'Orders',
  api_usage: 'AI / API services',
  console_activity: 'Agent Console activity',
  kirk_drafting: 'Drafting funnel',
  track_record: 'Track record & saves',
  quality_signals: 'Signals & threads',
  red_flags: 'Red flags',
};

function readerLabel(reader: string): string {
  return READER_LABELS[reader] ?? reader.replace(/_/g, ' ');
}

function Sparkline({ series }: { series: MetricSeries }) {
  const values = [...series.points].reverse().map((p) => p.value ?? 0);
  if (values.length < 2) return null;
  const max = Math.max(...values.map((v) => Math.abs(v)), 1);
  return (
    <span className={styles.spark} aria-hidden="true">
      {values.slice(-16).map((v, i) => (
        <i key={i} style={{ height: `${Math.max(2, (Math.abs(v) / max) * 22)}px` }} />
      ))}
    </span>
  );
}

function LiveSectionBlock({ section }: { section: LiveSection }) {
  return (
    <div data-testid={`live-${section.reader}`}>
      <div className={styles.sectionTitle}>{readerLabel(section.reader)}</div>
      {section.status === 'unavailable' && (
        <div className={styles.unavailable}>Unavailable right now{section.error ? `: ${section.error}` : ''}.</div>
      )}
      {section.status === 'empty' && <div className={styles.empty}>Nothing recorded in this window.</div>}
      {section.status === 'ok' && (
        <div className={styles.sectionGrid}>
          {section.metrics.map((m) => (
            <div key={m.metric_key} className={styles.kpi} title={m.detail ?? undefined}>
              <div className={styles.kpiLabel}>{m.label}</div>
              <div className={styles.kpiValue} style={{ fontSize: '1.15rem' }}>
                {formatMetric(m)}
              </div>
              {m.detail && <div className={styles.kpiSub}>{m.detail}</div>}
            </div>
          ))}
        </div>
      )}
      {section.status === 'ok' && (section.examples?.length ?? 0) > 0 && (
        <LiveExamplesTable reader={section.reader} label={section.examples_label} examples={section.examples ?? []} />
      )}
    </div>
  );
}

// Rows a reader shows beside its numbers so a person can check its calls
// (e.g. the threads the Kirk inbox gate filtered). Flagged rows arrive first.
function LiveExamplesTable({
  reader,
  label,
  examples,
}: {
  reader: string;
  label: string | null | undefined;
  examples: LiveExample[];
}) {
  const flagged = examples.filter((e) => e.flag).length;
  return (
    <div className={styles.examples} data-testid={`live-${reader}-examples`}>
      <p className={styles.examplesTitle}>
        {label ?? 'Examples'}
        <span className={styles.panelNote}>
          {flagged === 0 ? 'nothing flagged' : `${flagged} flagged`}
        </span>
      </p>
      <table className={styles.table}>
        <tbody>
          {examples.map((e) => (
            <tr key={e.key} className={e.flag ? styles.exampleFlagged : undefined}>
              <td>
                {e.title}
                {e.subtitle && <div className={styles.sub}>{e.subtitle}</div>}
              </td>
              <td className={styles.num}>
                {e.when ? formatStamp(e.when) : ''}
                {e.flag && <div className={`${styles.sub} ${styles.warn}`}>{e.flag}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NarrativeBlock({ narrative }: { narrative: Narrative }) {
  return (
    <>
      <p className={styles.narrativeHeadline}>{narrative.headline}</p>
      <div className={styles.narrativeBody}>
        <ReactMarkdown>{narrative.body_md}</ReactMarkdown>
      </div>
      {(narrative.highlights.length > 0 || narrative.risks.length > 0 || narrative.next_steps.length > 0) && (
        <div className={styles.lists}>
          {narrative.highlights.length > 0 && (
            <div>
              <p className={styles.listTitle}>Highlights</p>
              <ul className={styles.list}>
                {narrative.highlights.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </div>
          )}
          {narrative.risks.length > 0 && (
            <div>
              <p className={`${styles.listTitle} ${styles.warn}`}>Risks</p>
              <ul className={styles.list}>
                {narrative.risks.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {narrative.next_steps.length > 0 && (
            <div>
              <p className={styles.listTitle}>Next steps</p>
              <ul className={styles.list}>
                {narrative.next_steps.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className={styles.sub} style={{ marginTop: '0.6rem' }}>
        Written {formatStamp(narrative.as_of, true)} by {narrative.recorded_by}
      </div>
    </>
  );
}

export function SystemTab({ systemKey, windowDays, card }: Props) {
  const requestKey = `${systemKey}:${windowDays}`;
  const [result, setResult] = useState<{ key: string; data: SystemReadout | null; error: string | null } | null>(null);
  const data = result?.key === requestKey ? result.data : null;
  const error = result?.key === requestKey ? result.error : null;
  const loading = result?.key !== requestKey;

  useEffect(() => {
    let alive = true;
    readoutService
      .getSystem(systemKey, windowDays)
      .then((d) => alive && setResult({ key: requestKey, data: d, error: null }))
      .catch((err: unknown) =>
        alive &&
        setResult({
          key: requestKey,
          data: null,
          error: err instanceof Error ? err.message : 'Failed to load this system',
        }),
      );
    return () => {
      alive = false;
    };
  }, [systemKey, windowDays, requestKey]);

  if (error) return <div className={styles.error}>{error}</div>;
  if (loading || !data) return <div className={styles.loading}>Loading {card?.system.name ?? systemKey}…</div>;

  const { system, money } = data;
  const routes = (system.surfaces.routes as string[] | undefined) ?? [];
  const groups = groupLedger(data.ledger);
  const netClass = netTone(money) === 'good' ? styles.good : netTone(money) === 'bad' ? styles.bad : '';
  const hasValue = money.net_usd != null;
  const windowLabel = `last ${data.window_days} days`;

  return (
    <div data-testid={`system-tab-${system.key}`}>
      <div className={styles.systemHead}>
        <div>
          <h2 className={styles.systemName}>
            {system.name}
            <span className={`${styles.chip} ${system.status === 'active' ? styles.chipActive : ''}`}>{system.status}</span>
            <span className={styles.chip}>{systemCategoryLabel(system.category)}</span>
          </h2>
          {system.description && <p className={styles.systemDesc}>{system.description}</p>}
          {system.owner && <p className={styles.sub}>For {system.owner}</p>}
        </div>
        {routes.length > 0 && (
          <div className={styles.surfaces}>
            {routes.map((r) =>
              r.startsWith('/') && !r.endsWith('/') ? (
                <Link key={r} to={r}>
                  {r}
                </Link>
              ) : (
                <a key={r} href={r}>
                  {r}
                </a>
              ),
            )}
          </div>
        )}
      </div>

      <div className={styles.totals}>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Cost · {windowLabel}</div>
          <div className={styles.kpiValue}>{formatUsd(money.cost_usd)}</div>
          <div className={styles.kpiSub}>
            AI spend {formatUsd(money.ai_spend_usd)} · recorded {formatUsd(money.ledger_cost_usd)}
          </div>
        </div>
        <div className={styles.kpi} data-testid="system-benefit">
          <div className={styles.kpiLabel}>Benefit · {windowLabel}</div>
          <div className={styles.kpiValue}>{hasValue ? formatUsd(money.benefit_usd) : '—'}</div>
          <div className={styles.kpiSub}>
            {hasValue
              ? `verified ${formatUsd(money.verified_benefit_usd)} · estimated ${formatUsd(money.estimated_benefit_usd)} · ${money.benefit_lines} line${money.benefit_lines === 1 ? '' : 's'}`
              : 'not recorded yet'}
          </div>
        </div>
        <div className={styles.kpi} data-testid="system-net">
          <div className={styles.kpiLabel}>Net · {windowLabel}</div>
          <div className={`${styles.kpiValue} ${netClass}`}>{formatNet(money.net_usd)}</div>
          <div className={styles.kpiSub}>{hasValue ? 'benefit minus cost' : 'value not recorded yet'}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Funding · {windowLabel}</div>
          <div className={styles.kpiValue}>{formatUsd(money.funding_usd)}</div>
          <div className={styles.kpiSub}>{money.open_risks} open risk{money.open_risks === 1 ? '' : 's'}</div>
        </div>
      </div>

      <div className={styles.grid2}>
        <div>
          <div className={styles.panel}>
            <p className={styles.panelTitle}>
              Readout
              {data.narrative && <span className={styles.panelNote}>as of {formatStamp(data.narrative.as_of)}</span>}
            </p>
            {data.narrative ? (
              <NarrativeBlock narrative={data.narrative} />
            ) : (
              <div className={styles.empty}>
                No readout written yet. The curator writes one through <code>POST /api/internal/readout/narratives</code>.
              </div>
            )}
          </div>

          <div className={styles.panel}>
            <p className={styles.panelTitle}>
              Ledger
              <span className={styles.panelNote}>everything on record; window totals above</span>
            </p>
            {!hasValue && (
              <div className={styles.empty} data-testid="system-value-missing">
                No value recorded yet. Cost is live spend; Benefit and Net appear once the curator records a benefit line
                through <code>POST /api/internal/readout/ledger</code>.
              </div>
            )}
            {groups.length === 0 ? (
              <div className={styles.empty}>No cost, funding, benefit or risk lines recorded yet.</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th className={styles.num}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <GroupRows key={g.kind} group={g} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {data.narrative_history.length > 0 && (
            <div className={styles.panel}>
              <p className={styles.panelTitle}>Earlier readouts</p>
              <ul className={styles.history}>
                {data.narrative_history.map((n) => (
                  <li key={n.id}>
                    <span className={styles.sub}>{formatStamp(n.as_of)} · </span>
                    {n.headline}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div>
          <div className={styles.panel}>
            <p className={styles.panelTitle}>
              Live numbers
              <span className={styles.panelNote}>{windowLabel}, computed now</span>
            </p>
            {data.live.length === 0 ? (
              <div className={styles.empty}>No live reader wired for this system yet.</div>
            ) : (
              data.live.map((s) => <LiveSectionBlock key={s.reader} section={s} />)
            )}
          </div>

          <div className={styles.panel}>
            <p className={styles.panelTitle}>
              Recorded metrics
              <span className={styles.panelNote}>by the curator / automations</span>
            </p>
            {data.metrics.length === 0 ? (
              <div className={styles.empty}>Nothing recorded yet.</div>
            ) : (
              <table className={styles.table}>
                <tbody>
                  {data.metrics.map((s) => (
                    <tr key={s.metric_key}>
                      <td>
                        {s.label}
                        <div className={styles.sub}>
                          {s.latest.period_end ? periodLabel(s.latest.period_start, s.latest.period_end) : formatStamp(s.latest.as_of)}
                          {' · '}
                          {s.latest.recorded_by}
                        </div>
                      </td>
                      <td className={styles.num}>
                        <Sparkline series={s} /> <strong>{formatMetric({ ...s.latest, unit: s.unit })}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupRows({ group }: { group: ReturnType<typeof groupLedger>[number] }) {
  return (
    <>
      <tr className={styles.kindRow}>
        <td colSpan={3}>{group.label}</td>
        <td className={styles.num}>{formatUsd(group.total_usd)}</td>
      </tr>
      {group.entries.map((e) => (
        <tr key={e.id}>
          <td>
            {e.headline}
            <div className={styles.sub}>
              {categoryLabel(e.category)}
              {e.quantity != null && ` · ${e.quantity.toLocaleString('en-US')} ${e.quantity_unit ?? ''}`}
              {e.detail && ` · ${e.detail}`}
            </div>
          </td>
          <td className={styles.sub}>{periodLabel(e.period_start, e.period_end)}</td>
          <td>
            <span
              className={`${styles.badge} ${e.status === 'verified' ? styles.badgeVerified : e.status === 'rejected' ? styles.badgeRejected : ''}`}
            >
              {e.status}
            </span>
            <div className={styles.sub}>{e.confidence} confidence</div>
          </td>
          <td className={styles.num}>{e.amount_usd == null ? '—' : formatUsd(e.amount_usd)}</td>
        </tr>
      ))}
    </>
  );
}
